import { useState, useEffect } from "react";

import { TransactionStatus } from "../../common/constans";
import ApiClient from "../../ApiClient";

const apiClient = ApiClient();

const usePaymentLink = (
    sessionId, profileName, orderRef, merchantId) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingPaymentDetails, setIsFetchingPaymentDetails] = useState(true);
    const [isFetchingSessionInfo, setIsFetchingSessionInfo] = useState(true);
    const [isFetchingToken, setIsFetchingToken] = useState(true);
    const [isFetchingMerchantLogo, setIsFetchingMerchantLogo] = useState(true);
    const [isPaying, setIsPaying] = useState(false);
    const [paymentResponseDto, setPaymentResponseDto] = useState({});
    const [sessionInfo, setSessionInfo] = useState(null);
    const [paymentLinkDetails, setPaymentLinkDetails] = useState(null);
    const [merchantLogo, setMerchantLogo] = useState(null);
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);

    const { 
        pay,
        getTransactionSessionInfo,
        getLiveLinkDetails,
        getMerchantLogo,
        initLiveLinkPayment } = apiClient;

    useEffect(() => {
        if (!profileName || !orderRef) return;

        const getTokenFromFullUrl = (url) => {
            const parsedUrl = new URL(url);
            return parsedUrl.searchParams.get("token");
        };

        const onSuccess = (response) => {
            console.log("token: ", response);
            if (response.succeeded === true) {
                setToken(getTokenFromFullUrl(response.response.checkout_url));
            } else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsFetchingToken(false);
            setIsFetchingSessionInfo(false);
        }

        const fetch = async () => {
            try {
                setIsFetchingToken(true);
                await initLiveLinkPayment(profileName, {}, orderRef, onSuccess, onFail);
            } catch (e) {
                setError(e);
            } finally {
                setIsFetchingToken(false);
            }
        }

        fetch();
    }, [profileName, orderRef, initLiveLinkPayment]);

    useEffect(() => {
        if (!token) return;

        const onSuccess = (response) => {
            if (response.succeeded === true) {
                setSessionInfo(response);
            }
            else {
                onFail(response);
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
            setError(response.error_message);
            setIsFetchingSessionInfo(false);
        }

        const fetch = async () => {
            try {
                setIsFetchingSessionInfo(true);
                await getTransactionSessionInfo(token, sessionId, onSuccess, onFail);
            } catch (e) {
                setError(e);
            } finally {
                setIsFetchingSessionInfo(false);
            }
        }

        fetch();

    }, [token, sessionId, getTransactionSessionInfo]);

    useEffect(() => {
        if (!orderRef) return;

        const onGetLiveLinkDetailsSuccess = (response) => {
            if (response.succeeded === true) {
                setPaymentLinkDetails(response.response);
            }
            else {
                setError(response.error_message);
            }
        }

        const onFail = () => {
            setError("Couldn't fetch payment details");
        }

        const fetchDetails = async () => {
            try {
                setIsFetchingPaymentDetails(true); 
                await getLiveLinkDetails(profileName, orderRef, onGetLiveLinkDetailsSuccess, onFail);
            } catch (err) {
                setError(err);
            } finally {
                setIsFetchingPaymentDetails(false); 
            }
        };

        fetchDetails();
    }, [orderRef, profileName, getLiveLinkDetails]);

    useEffect(() => {
        if (!merchantId) return;

        const onGetMerchantLogoSuccess = (response) => {
            setMerchantLogo(response.response.merchant_logo);
        }

        const fetchLogo = async () => {
            try {
                setIsFetchingMerchantLogo(true);
                await getMerchantLogo(merchantId, onGetMerchantLogoSuccess, () => {});
            } catch (err) {
                console.log(err);
            } finally {
                setIsFetchingMerchantLogo(false);
            }
        }

        fetchLogo();
    }, [merchantId, getMerchantLogo]);

    useEffect(() => {
        setIsLoading(isFetchingPaymentDetails | isFetchingSessionInfo | isFetchingToken | isFetchingMerchantLogo);
    }, [isFetchingToken, isFetchingPaymentDetails, isFetchingSessionInfo, isFetchingMerchantLogo]);

    const submitPayment = async (formData) => {
        const getExpirationMonth = () => {
            return +formData.expiry_date.substring(0, 2);
        }

        const getExpirationYear = () => {
            const year = Math.floor(new Date().getFullYear() / 100) * 100;
            return year + (+formData.expiry_date.substring(3, 5));
        }

        const getPaymentTransactionRequestDto = (formData) => {
            return {
                "payment_number": formData.payment_number.replace(/ /g, ''),
                "expiration_month": getExpirationMonth(),
                "expiration_year": getExpirationYear(),
                "first_name": formData.first_name,
                "last_name": formData.last_name,
                "security_code": formData.cvv,
                "should_save_payment_method": formData.should_save_payment_method,
                "session_id": sessionId,
            }
        }

        const onSuccess = (responseDto) => {
            setPaymentResponseDto(responseDto);
            if (responseDto?.succeeded) {
                if (!responseDto.error_message && responseDto.transaction_status === TransactionStatus.Successful) {
                    window.location.replace(`${responseDto.redirection_url}?${new URLSearchParams(responseDto).toString()}`);
                } else {
                    const errorMessage = responseDto.error_message ?? "Payment failed";
                    window.location.replace(`${responseDto.redirection_url}?error_code=${responseDto.error_code}&error_message=${errorMessage}`);
                }
            } else if (responseDto?.succeeded === false) {
                setError(responseDto.error_message);
            } else {
                setError("Something went wrong");
            }
        }

        const onFail = (response) => {
            console.log("fail: ", response);
        }

        try {
            setIsPaying(true);
            await pay(getPaymentTransactionRequestDto(formData), token, onSuccess, onFail);
        } catch (err) {
            setError(err);
        } finally {
            setIsPaying(false);
        }
    };

    return {
        isLoading,
        sessionInfo,
        error,
        paymentLinkDetails,
        merchantLogo,
        paymentResponseDto,
        isPaying,
        submitPayment };
};

export default usePaymentLink;
